import { useEffect, useState } from "react";
import api, { eur, eur2 } from "@/lib/api";
import { STATUS_STYLES } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line,
} from "recharts";
import {
  Inbox, FileText, CheckCircle2, TrendingUp, Wallet, Calculator, Loader2,
} from "lucide-react";

const KPI = [
  { key: "monthly_requests", title: "Demandes ce mois", icon: Inbox, color: "text-sky-600", bg: "bg-sky-50", testid: "kpi-monthly-requests", fmt: (v) => v },
  { key: "quotes_sent", title: "Devis envoyés", icon: FileText, color: "text-amber-600", bg: "bg-amber-50", testid: "kpi-quotes-sent", fmt: (v) => v },
  { key: "signed_projects", title: "Chantiers signés", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", testid: "kpi-signed-projects", fmt: (v) => v },
  { key: "conversion_rate", title: "Taux de conversion", icon: TrendingUp, color: "text-blue-600", bg: "bg-blue-50", testid: "kpi-conversion-rate", fmt: (v) => `${v} %` },
  { key: "total_commissions_month", title: "Commissions du mois", icon: Wallet, color: "text-violet-600", bg: "bg-violet-50", testid: "kpi-total-commissions", fmt: (v) => eur(v) },
  { key: "avg_project_value", title: "Montant moyen chantier", icon: Calculator, color: "text-rose-600", bg: "bg-rose-50", testid: "kpi-avg-project-value", fmt: (v) => eur(v) },
];

const STATUS_COLORS = {
  "Nouvelle": "#0284c7", "Artisan assigné": "#4f46e5", "Devis envoyé": "#d97706",
  "Chantier accepté": "#2563eb", "Terminé": "#059669", "Annulé": "#e11d48",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((res) => setStats(res.data)).catch(() => {});
  }, []);

  if (!stats) {
    return <div className="grid place-items-center py-40"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }

  const statusData = Object.entries(stats.status_counts).map(([name, value]) => ({ name, value }));

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Tableau de bord</h1>
          <p className="text-slate-500 mt-1">Vue d'ensemble de votre activité de mise en relation.</p>
        </div>
        <div className="rounded-xl bg-[#0B1E4D] text-white px-5 py-3">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Commissions totales cumulées</p>
          <p className="font-mono-nums text-2xl font-extrabold" data-testid="total-commissions-all">{eur2(stats.total_commissions_all)}</p>
        </div>
      </div>

      {/* KPI grid */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4">
        {KPI.map((k) => (
          <div key={k.key} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid={k.testid}>
            <div className={`h-10 w-10 rounded-xl ${k.bg} grid place-items-center`}>
              <k.icon className={`h-5 w-5 ${k.color}`} />
            </div>
            <p className="mt-4 text-sm text-slate-500 font-medium">{k.title}</p>
            <p className="font-mono-nums text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">{k.fmt(stats[k.key])}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-display text-lg font-semibold text-slate-900">Demandes par statut</h2>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {statusData.map((d) => <Cell key={d.name} fill={STATUS_COLORS[d.name]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-display text-lg font-semibold text-slate-900">Évolution sur 6 mois</h2>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.trend} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip />
                <Line type="monotone" dataKey="demandes" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} name="Demandes" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h2 className="font-display text-lg font-semibold text-slate-900">Répartition par catégorie</h2>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(stats.category_counts).map(([cat, count]) => (
            <div key={cat} className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-100 px-4 py-3">
              <span className="text-sm font-medium text-slate-700">{cat}</span>
              <span className="font-mono-nums font-bold text-slate-900">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
